/**
 * Invoice Data Fetch Service
 *
 * This module handles the fetching and processing of invoice data for the invoice management module, including:
 *  - Retrieving invoice data from the API based on project and filter criteria
 *  - Fetching project details, including stages and tasks, for hierarchy navigation
 *  - Processing supplier and status options for filtering
 *  - Aggregating summary statistics (total invoices, amount, suppliers, etc.)
 *  - Updating state with fetched data for invoices, projects, filter options, and breadcrumbs
 *  - Handling errors and edge cases during data retrieval
 *
 * Supports dynamic filtering and project-specific invoice data retrieval.
 */
import { getInvoiceManagementList } from "../../../actions/project-management.action";
import moment from 'moment';
import _ from 'lodash';
import { toDecimal, roundValue } from "../../../util/decimal-utils";
import { TaskType } from "../../../util/enums";
import { INVOICE_SPECIAL_IDS, INVOICE_ERROR_MESSAGES, INVOICE_STATUS, INVOICE_FILTER_OPTIONS } from "../../../util/enums/invoice-management";

export const fetchInvoiceData = async (dispatch, initialFilterFields, setProjects, setInvoices, setSummaryStats, setFilterState, setBreadcrumbItems) => {
    try {
        const response = await dispatch(getInvoiceManagementList());

        // Validate response
        if (!response) {
            console.warn('No response received from getInvoiceManagementList');
            resetState(setProjects, setInvoices, setSummaryStats, setFilterState, setBreadcrumbItems, initialFilterFields);
            return;
        }

        const data = Array.isArray(response) ? response : [];

        // Handle empty or invalid response
        if (data.length === 0) {
            console.warn('No invoice data received from API');
            resetState(setProjects, setInvoices, setSummaryStats, setFilterState, setBreadcrumbItems, initialFilterFields);
            return;
        }

        const processedProjects = [];
        const allInvoices = [];
        const uniqueSuppliers = new Map();
        const overallPaymentStatus = new Set();
        const uniquePOIds = new Set(); // Track unique PO IDs
        const uniquePOIdsWithInvoices = new Set(); // Track unique PO IDs that have invoices

        // Helper function to process tasks or variations and their invoices
        const processItem = (item, projectId, projectName, stageId = null, stageName = null, type = TaskType.TASK, stageHierarchy) => {
            const isVariation = type === TaskType.VARIATION;
            const sectionHierarchy = stageId
                ? stageHierarchy
                : isVariation
                    ? INVOICE_SPECIAL_IDS.VARIATIONS || INVOICE_SPECIAL_IDS.UNAMED_VARIATION
                    : stageName || INVOICE_STATUS.NA;

            const itemObj = {
                id: item.id,
                name: item.name,
                supplierAmountPaid: item.supplierAmountPaid || 0,
                isActive: false,
                projectId,
                sectionHierarchy: sectionHierarchy.replace(/>/g, '\\'),
                type,
                ...(isVariation ? { stageId: item.stageId || null, is_variation_deleted: item.is_variation_deleted || false } : {}),
            };

            if (item.PurchaseOrders && Array.isArray(item.PurchaseOrders)) {
                item.PurchaseOrders.forEach((po) => {
                    // Track unique PO IDs
                    if (po.id) {
                        if (!po?.invoice?.id) return;
                        uniquePOIds.add(po.id);
                    }

                    if (po.invoice) {
                        // Track unique PO IDs that have invoices
                        if (po.id) uniquePOIdsWithInvoices.add(po.id);

                        const status = po.invoicestatus;
                        const amountPaidDecimal = toDecimal(po.invoice.amountPaid || 0);
                        const taxAmountDecimal = toDecimal(po.invoice.taxAmount || 0);
                        const calculatedAmountPaid = roundValue(amountPaidDecimal.minus(taxAmountDecimal));
                        const issueDate = po.delivery_date ? moment(po.delivery_date).format('DD/MM/YYYY') : INVOICE_STATUS.NA;

                        if (issueDate === INVOICE_STATUS.NA) {
                            console.warn(`Invalid Po delivery date for invoice: ${po.invoice.id}`, po.delivery_date);
                        }

                        allInvoices.push({
                            invoiceId: po.invoice.id,
                            invoiceNumber: po.invoice.invoice_no || INVOICE_STATUS.NA,
                            supplier: po.vendor?.business_name || INVOICE_ERROR_MESSAGES.UNKNOWN_SUPPLIER,
                            issueDate,
                            dueDate: po.invoice.dueDate ? moment(po.invoice.dueDate).format('DD/MM/YYYY') : INVOICE_STATUS.NA,
                            status,
                            totalAmount: roundValue(amountPaidDecimal),
                            subTotal: calculatedAmountPaid,
                            items: po.invoice.items || [],
                            PurchaseOrders: po,
                            projectId,
                            stageId: isVariation ? item.stageId || INVOICE_SPECIAL_IDS.VARIATIONS : stageId,
                            taskId: item.id,
                            sectionHierarchy: itemObj.sectionHierarchy,
                            project: { id: projectId, name: projectName },
                            stage: stageId ? { id: stageId, name: stageName } : null,
                            task: { id: item.id, name: item.name, type },
                            userId: po.invoice.userId,
                            userName: po.invoice.userName,
                        });

                        if (po.vendorId && po.vendor && !uniqueSuppliers.has(po.vendorId)) {
                            uniqueSuppliers.set(po.vendorId, po.vendor.business_name);
                        }
                        overallPaymentStatus.add(status);
                    }
                });
            }

            return itemObj;
        };

        // Helper function to determine invoice status
        // const determineInvoiceStatus = (invoice) => {
        //     if (invoice.status === INVOICE_STATUS.PAID) return INVOICE_STATUS.PAID;
        //     if (invoice.status !== INVOICE_STATUS.PAID && invoice.status !== INVOICE_STATUS.PARTIAL_PAID) return INVOICE_STATUS.PENDING;
        //     return INVOICE_STATUS.PARTIAL_PAID;
        // };

        // Helper function to process stages recursively
        const processStage = (stage, projectId, projectName, parentStage = null) => {
            const sectionHierarchy = parentStage
                ? `${parentStage.sectionHierarchy} > ${stage.name}`
                : stage.sectionHierarchy || stage.name;

            const stageItem = {
                id: stage.id,
                name: stage.name,
                supplierAmountPaid: stage.supplierAmountPaid || 0,
                isActive: false,
                tasks: [],
                variations: [],
                stages: [],
                sectionHierarchy,
            };

            if (stage.tasks && Array.isArray(stage.tasks)) {
                stageItem.tasks = stage.tasks.map((task) =>
                    processItem(task, projectId, projectName, stage.id, stage.name, task.type || TaskType.TASK, sectionHierarchy)
                );
            }

            if (stage.stages && Array.isArray(stage.stages)) {
                stageItem.stages = stage.stages.map((nestedStage) =>
                    processStage(nestedStage, projectId, projectName, stage)
                );
            }

            return stageItem;
        };

        // Process each project
        data.forEach((project) => {
            const projectItem = {
                id: project.id,
                name: project.name,
                suppliers: project.supplierCount || 0,
                amount: roundValue(toDecimal(project.supplierAmountPaid || 0)),
                isActive: false,
                stages: [],
                variations: [],
                deletedVariations: [],
            };

            // Process stages
            if (project.stages && Array.isArray(project.stages)) {
                projectItem.stages = project.stages.map((stage) => processStage(stage, project.id, project.name));
            }

            // Collect all variations at project level first
            if (project.variations && Array.isArray(project.variations)) {
                projectItem.variations = project.variations.map((variation) =>
                    processItem(variation, project.id, project.name, null, null, TaskType.VARIATION, variation.sectionHierarchy || INVOICE_STATUS.NA)
                );
            }

            // Distribute variations with stageId to their respective stages
            projectItem.variations.forEach((variation) => {
                if (variation.stageId) {
                    const stage = projectItem.stages.find(s => s.id === variation.stageId);
                    if (stage) {
                        stage.variations.push(variation);
                    }
                }
            });

            // Filter out variations with stageId from project-level variations
            projectItem.variations = projectItem.variations.filter(v => !v.stageId);

            // Process deleted variations
            if (project.deletedVariations && Array.isArray(project.deletedVariations)) {
                projectItem.deletedVariations = project.deletedVariations.map((variation) =>
                    processItem(variation, project.id, project.name, null, null, TaskType.VARIATION)
                );
            }

            processedProjects.push(projectItem);
        });

        // Set the processed data to state
        setProjects(processedProjects);
        setInvoices(allInvoices);

        // Calculate summary stats using decimal.js for precision
        const totalAmountDecimal = allInvoices.reduce((sum, invoice) => {
            return sum.plus(toDecimal(invoice.totalAmount || 0));
        }, toDecimal(0));

        const summaryStats = {
            totalInvoices: allInvoices.length,
            totalAmount: roundValue(totalAmountDecimal),
            totalSuppliers: uniqueSuppliers.size,
            totalPOs: uniquePOIdsWithInvoices.size,
            paymentStatus: overallPaymentStatus.size > 0 ? Array.from(overallPaymentStatus).join(', ') : INVOICE_STATUS.NA,
        };

        setSummaryStats(summaryStats);

        // Set supplier filter options
        const supplierOptions = Array.from(uniqueSuppliers.entries()).map(([id, name]) => ({
            label: name,
            value: id,
        }));

        setFilterState((prevState) => ({
            ...prevState,
            supplierId: {
                ...prevState.supplierId,
                options: [{ label: INVOICE_FILTER_OPTIONS.ALL_SUPPLIERS, value: INVOICE_FILTER_OPTIONS.ALL_SUPPLIERS }, ..._.orderBy(supplierOptions, ['label'], ['asc'])],
            },
        }));

        setBreadcrumbItems([]); // Adjust if breadcrumbs need to include variations
    } catch (error) {
        console.error('Failed to fetch invoice management data:', error);
        resetState(setProjects, setInvoices, setSummaryStats, setFilterState, setBreadcrumbItems, initialFilterFields);
    }
};

// Helper function to reset state on empty data or error
const resetState = (setProjects, setInvoices, setSummaryStats, setFilterState, setBreadcrumbItems, initialFilterFields) => {
    setProjects([]);
    setInvoices([]);
    setSummaryStats({
        totalInvoices: 0,
        totalAmount: '0.00',
        totalSuppliers: 0,
        paymentStatus: INVOICE_STATUS.NA,
        totalPOs: 0,
    });
    setBreadcrumbItems([]);
    setFilterState((prevState) => ({
        ...initialFilterFields,
        supplierId: {
            ...initialFilterFields.supplierId,
            options: prevState.supplierId.options || [],
        },
    }));
};